import { createApp, h } from 'vue';
import ElementPlus from 'element-plus';
import 'element-plus/dist/index.css';
import 'element-plus/theme-chalk/display.css';
import 'swiper/css';
import moment from 'moment';

import JsonCSV from 'vue-json-csv';

//https://vue-select.org/api/props.html
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

// Set the components prop default to return our fresh components
vSelect.props.components.default = () => ({
	OpenIndicator: {
		render: () => h('span', { class: 'select-arrow' }),
	},
});

// @ts-ignore
import App from './App.vue';
import router from './router';
import store, { key } from './store';
import { toRaw } from 'vue';

import { categories } from './utils/categories';

import {
	getStateBeschwerdeLabel,
	getStateBeschwerdeColor,
	getStateFallLabel,
	getStateFallColor,
	getStateEntscheidungColor,
	getStateEntscheidungLabel,
	getStateStellungnahmeLabel,
	getStateUserLabel,
	getStateUserColor,
	getStateStellungnahmeColor,
} from './utils/functions';
import { Category } from './types/types';

const app = createApp(App);

app.config.globalProperties.$formatTimestamp = (ts: number, format = 'DD.MM.YYYY', isMilliseconds = false) => {
	if (ts) {
		if (isMilliseconds) ts /= 1000;
		return moment.unix(ts).format(format);
	}
	return '';
};

app.config.globalProperties.$formatDate = (date: string, format = 'DD.MM.YYYY') => {
	if (date) {
		return moment(date).format(format);
	}
	return '';
};

app.config.globalProperties.$getCountErgebnisseLabel = (cnt: number, label?: string) => {
	switch (label) {
		case 'Fälle':
			return cnt === 1 ? cnt + ' Fall' : cnt + ' Fälle';
		case 'Ergebnisse':
		default:
			return cnt === 1 ? cnt + ' Ergebnis' : cnt + ' Ergebnisse';
	}
};

app.config.globalProperties.$getStateFullLabel = (type: string, stateKey, short: boolean = true): string => {
	switch (type) {
		case 'complaint':
			return getStateBeschwerdeLabel(stateKey, short);
		case 'case':
			return getStateFallLabel(stateKey, short);
		case 'decision':
			return getStateEntscheidungLabel(stateKey, short);
		case 'statement':
			return getStateStellungnahmeLabel(stateKey, short);
		case 'user':
			return getStateUserLabel(stateKey, short);
		default:
			return '';
	}
};

app.config.globalProperties.$getStateColor = (type: string, stateKey) => {
	switch (type) {
		case 'complaint':
			return getStateBeschwerdeColor(stateKey);
		case 'case':
			return getStateFallColor(stateKey);
		case 'decision':
			return getStateEntscheidungColor(stateKey);
		case 'user':
			return getStateUserColor(stateKey);
		case 'statement':
			return getStateStellungnahmeColor(stateKey);
		default:
			return '';
	}
};

app.config.globalProperties.$recursivelyFindCategoryName = (categoryNumber: string): string => {
	const constantCategories = categories as Category[];

	const findCategoryName = (categories: Category[], categoryNumber: string): string | null => {
		for (const category of categories) {
			if (category.number === categoryNumber) {
				return category.name || '';
			}
			if (category.children && category.children.length > 0) {
				const name = findCategoryName(category.children, categoryNumber);
				if (name) {
					return name;
				}
			}
		}
		return null;
	};

	const result = findCategoryName(constantCategories, categoryNumber);
	return result !== null ? result : 'Category not found';
};

app.config.globalProperties.$sortAlphabetically = (a: string, b: string, field: string): number => {
	const titleA = a[field].toLowerCase();
	const titleB = b[field].toLowerCase();

	if (titleA < titleB) return -1;
	if (titleA > titleB) return 1;
	return 0;
};

app.config.globalProperties.$sortAttachments = (a: any, b: any): number => {
	const hasAttachmentA = a.files.length > 0 || a.links.length > 0;
	const hasAttachmentB = b.files.length > 0 || b.links.length > 0;

	if (hasAttachmentA === hasAttachmentB) return 0;
	if (hasAttachmentA && !hasAttachmentB) return -1;
	return 1;
};

app.config.globalProperties.$sortDates = (dateA: string, dateB: string): number => {
	if (dateA && dateB) {
		const dateAObj = new Date(dateA);
		const dateBObj = new Date(dateB);

		if (dateAObj < dateBObj) return -1;
		if (dateAObj > dateBObj) return 1;
		return 0;
	}

	if (dateA && !dateB) return -1;
	if (!dateA && dateB) return 1;
	return 0;
};

app.config.globalProperties.$getCategoryNameByNumber = (number: string): string => {
	let cat = categories.find((c) => c.number === number);

	if (cat && cat.name) {
		return cat.name;
	} else return number;
};

/* SENTRY */
import * as Sentry from '@sentry/vue';

Sentry.init({
	app,
	dsn: 'https://5fa7f091d1a546989b60df4610214906@o4506654993547264.ingest.us.sentry.io/4509090078654464',
});

app.use(store, key);
app.use(router);
app.use(ElementPlus);
app.component('v-select', vSelect);
app.component('downloadCsv', JsonCSV);
app.mount('#app');
